import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { authAPI } from '../services/api';
import freshroadlogo from '../images/freshroadlogo.png';

const VerifyEmail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  const { userId, email } = location.state || {};
  
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [userLevel, setUserLevel] = useState(0);
  const [loading, setLoading] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (!userId || !email) {
      navigate('/login');
    }
  }, [userId, email, navigate]);

  useEffect(() => {
    let timer;
    if (cooldown > 0) {
      timer = setTimeout(() => setCooldown(cooldown - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [cooldown]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (code.length !== 6) {
      setError('Please enter a valid 6-digit code');
      setLoading(false);
      return;
    }

    try {
      const response = await authAPI.verifyEmail({ userId, code });

      if (response.data.success) {
        setSuccess(true);
        setUserLevel(response.data.userlevel);
      }
    } catch (error) {
      const errorData = error.response?.data;
      setError(errorData?.message || 'Invalid or expired verification code');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setError('');
    
    try {
      const response = await authAPI.resendVerification({ userId });

      if (response.data.success) {
        setCooldown(60);
        setError('');
      }
    } catch (error) {
      const errorData = error.response?.data;
      if (errorData?.error === 'COOLDOWN') {
        setCooldown(errorData.timeRemaining);
        setError(errorData.message);
      } else {
        setError(errorData?.message || 'Failed to resend code');
      }
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8 text-center">
          <img
            className="mx-auto h-12 w-auto"
            src={freshroadlogo}
            alt="Freshroad"
          />
          
          <div className="bg-white rounded-lg shadow-md p-8">
            <div className="mb-4">
              <svg className="mx-auto h-12 w-12 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Email Verified!
            </h2>
            
            {userLevel < 100 ? (
              <div>
                <p className="text-gray-700 mb-4">
                  Your email has been verified successfully. Your registration is awaiting approval from an administrator.
                </p>
                <p className="text-gray-600 mb-6">
                  You will receive an email notification when your account has been approved and granted access.
                </p>
                <Link
                  to="/login"
                  className="inline-block px-6 py-2 bg-cyan-600 text-white rounded-md hover:bg-cyan-700"
                >
                  Return to Login
                </Link>
              </div>
            ) : (
              <div>
                <p className="text-gray-700 mb-6">
                  Your email has been verified successfully. You can now login to your account.
                </p>
                <Link
                  to="/login"
                  className="inline-block px-6 py-2 bg-cyan-600 text-white rounded-md hover:bg-cyan-700"
                >
                  Go to Login
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <img
            className="mx-auto h-12 w-auto"
            src={freshroadlogo}
            alt="Freshroad"
          />
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Verify your email
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            We've sent a 6-digit verification code to
            <br />
            <span className="font-medium text-cyan-600">{email}</span>
          </p>
          <p className="mt-2 text-center text-xs text-gray-500">
            The code will expire in 30 minutes
          </p>
        </div>
        
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          {error && (
            <div className="rounded-md bg-red-50 p-4">
              <div className="text-sm text-red-800">{error}</div>
            </div>
          )}
          
          <div>
            <label htmlFor="code" className="block text-sm font-medium text-gray-700 mb-2 text-center">
              Enter verification code
            </label>
            <input
              id="code"
              name="code"
              type="text"
              maxLength="6"
              required
              className="appearance-none relative block w-full px-3 py-3 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-cyan-500 focus:border-cyan-500 text-center text-2xl tracking-widest font-bold"
              placeholder="000000"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
            />
          </div>

          <div>
            <button
              type="submit"
              disabled={loading || code.length !== 6}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-cyan-600 hover:bg-cyan-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Verifying...' : 'Verify Email'}
            </button>
          </div>

          <div className="text-center">
            <button
              type="button"
              onClick={handleResend}
              disabled={cooldown > 0}
              className="font-medium text-cyan-600 hover:text-cyan-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {cooldown > 0 ? `Resend code (${cooldown}s)` : 'Resend verification code'}
            </button>
            {cooldown > 0 && (
              <p className="mt-1 text-xs text-gray-500">
                Re-send will be available in {cooldown} seconds cooldown
              </p>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default VerifyEmail;

